// React Query hooks for the WB weighbridges + calibrations admin surface
// (Phase C2). Mirrors server/src/modules/wb/weighbridges/types.ts.
//
// `indicator_config_json` is encrypted/secret server-side and is NEVER
// returned in any DTO. On the form it's a one-way write: the client
// composes a stringified JSON blob and posts it; on edit-load the field
// is null and the UI shows a "re-enter to update" affordance.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types ------------------------------------------------------

export interface WbYardSummary {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface WbCurrentCalibration {
  readonly enc_id: string;
  /** YYYY-MM-DD */
  readonly valid_until: string;
}

export interface WbWeighbridge {
  readonly enc_id: string;
  readonly yard: WbYardSummary;
  readonly name: string;
  readonly code: string;
  readonly max_capacity_kg: number | null;
  readonly min_threshold_kg: number | null;
  readonly indicator_protocol: string | null;
  readonly is_trade_approved: boolean;
  readonly stable_tolerance_kg: number;
  readonly stable_duration_ms: number;
  readonly status: 'online' | 'offline' | 'maintenance';
  /** Real agent presence for this bridge: true when a pinned agent has a fresh
   *  heartbeat (90s). Distinct from `status` (admin-set hardware state). */
  readonly agent_online: boolean;
  readonly current_calibration: WbCurrentCalibration | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface WbCalibration {
  readonly enc_id: string;
  readonly certificate_number: string;
  readonly certifier: string;
  /** YYYY-MM-DD */
  readonly calibrated_at: string;
  /** YYYY-MM-DD */
  readonly valid_until: string;
  readonly certificate_url: string | null;
  readonly notes: string | null;
  readonly created_at: string;
}

export interface CreateWeighbridgeInput {
  yard_id: string;
  name: string;
  code: string;
  max_capacity_kg?: number;
  min_threshold_kg?: number;
  indicator_protocol?: string;
  indicator_config_json?: string;
  is_trade_approved?: boolean;
  stable_tolerance_kg?: number;
  stable_duration_ms?: number;
  status?: 'online' | 'offline' | 'maintenance';
}

export interface UpdateWeighbridgeInput {
  name?: string;
  code?: string;
  max_capacity_kg?: number | null;
  min_threshold_kg?: number | null;
  indicator_protocol?: string | null;
  indicator_config_json?: string | null;
  is_trade_approved?: boolean;
  stable_tolerance_kg?: number;
  stable_duration_ms?: number;
  status?: 'online' | 'offline' | 'maintenance';
}

export interface CreateCalibrationInput {
  certificate_number: string;
  certifier: string;
  /** YYYY-MM-DD */
  calibrated_at: string;
  /** YYYY-MM-DD */
  valid_until: string;
  certificate_url?: string | null;
  notes?: string | null;
}

// -- Queries ----------------------------------------------------------------

export interface UseWbWeighbridgesOpts {
  /** When true, poll every 60s (used by the dashboard live tile row). */
  enablePolling?: boolean;
}

/**
 * The online/offline/maintenance badge shown for a weighbridge. `status` is
 * the admin-set operational state (editable on the detail form) and is only
 * consulted for the explicit `maintenance` override; everywhere else the
 * badge reflects real agent presence (`agent_online`, derived server-side
 * from a live heartbeat), not the — possibly stale — admin-set value.
 *
 * Single source for both the Dashboard tile (`WbBridgeTile`) and the
 * Settings list (`WbWeighbridgesPage`) so the two surfaces can't disagree —
 * see docs/S63_weighbridge_pipeline_findings.md (Part E) for the bug this
 * fixes: the Settings page previously rendered raw `status` directly.
 */
export function deriveWbDisplayStatus(
  w: Pick<WbWeighbridge, 'status' | 'agent_online'>,
): WbWeighbridge['status'] {
  if (w.status === 'maintenance') return 'maintenance';
  return w.agent_online ? 'online' : 'offline';
}

export function useWbWeighbridges(
  filters: { yard_id?: string; is_active?: boolean } = {},
  opts: UseWbWeighbridgesOpts = {},
) {
  return useQuery<{ weighbridges: WbWeighbridge[] }>({
    queryKey: queryKeys.wb.weighbridges(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<{ weighbridges: WbWeighbridge[] }>(
        endpoints.wb.weighbridges,
        Object.keys(filters).length > 0 ? { params: filters } : undefined,
      );
      return res.data;
    },
    staleTime: 30_000,
    refetchInterval: opts.enablePolling ? 60_000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useWbWeighbridge(
  encId: string | undefined,
  opts: UseWbWeighbridgesOpts = {},
) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ weighbridge: WbWeighbridge }>({
    queryKey: queryKeys.wb.weighbridge(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbWeighbridge called without enc_id');
      const res = await apiClient.get<{ weighbridge: WbWeighbridge }>(
        endpoints.wb.weighbridge(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
    // The ticket page polls so `agent_online` reflects live presence.
    refetchInterval: opts.enablePolling ? 30_000 : false,
    refetchIntervalInBackground: false,
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateWeighbridge() {
  const qc = useQueryClient();
  return useMutation<{ weighbridge: WbWeighbridge }, Error, CreateWeighbridgeInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ weighbridge: WbWeighbridge }, CreateWeighbridgeInput>(
        endpoints.wb.weighbridges,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.weighbridges() });
    },
  });
}

export function useUpdateWeighbridge(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ weighbridge: WbWeighbridge }, Error, UpdateWeighbridgeInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ weighbridge: WbWeighbridge }, UpdateWeighbridgeInput>(
        endpoints.wb.weighbridge(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.weighbridges() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.weighbridge(encId) });
    },
  });
}

export function useDeleteWeighbridge(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(endpoints.wb.weighbridge(encId));
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.weighbridges() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.weighbridge(encId) });
    },
  });
}

// -- Calibrations -----------------------------------------------------------

export function useCalibrations(weighbridgeEncId: string | undefined) {
  const enabled = !!weighbridgeEncId && weighbridgeEncId !== 'new';
  return useQuery<{ calibrations: WbCalibration[] }>({
    queryKey: queryKeys.wb.calibrations(weighbridgeEncId ?? ''),
    queryFn: async () => {
      if (!weighbridgeEncId) throw new Error('useCalibrations called without enc_id');
      const res = await apiClient.get<{ calibrations: WbCalibration[] }>(
        endpoints.wb.calibrations(weighbridgeEncId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateCalibration(weighbridgeEncId: string) {
  const qc = useQueryClient();
  return useMutation<{ calibration: WbCalibration }, Error, CreateCalibrationInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { calibration: WbCalibration },
        CreateCalibrationInput
      >(endpoints.wb.calibrations(weighbridgeEncId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.wb.calibrations(weighbridgeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.wb.weighbridge(weighbridgeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.weighbridges() });
    },
  });
}
